import { Inject } from '@odinjs/odin';
import moment from 'moment';

import Controller, { OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import FieldAPI from '@philips-internal/oswald-ui-components/lib/components/field/field-api';
import { ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HttpResponse, HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { FormAPI, InputAPI, PanelAPI } from '@philips-internal/oswald-ui-components/lib/components';

import BaseController from '../../../../../controllers/BaseController';
import InformationBarUtils from '../../../../commons/header/information-bar-header-utils';
import { MedicineViewResponseType } from './medicine-types';
import { Endpoint } from '../../../../../constants/endpoints';
import { ISO_DATE_INPUT } from '../../../../../constants/date-utils';
import { FragmentNames, MedicineViewFragmentAliases } from '../../../../../codegen';
import { AdministrationType,  DurationType } from '../../cpoe-view-types';

@Controller({ fragment: FragmentNames.MEDICINE_VIEW_FRAGMENT })
class MedicineViewController extends BaseController {

  @Inject(MedicineViewFragmentAliases.PANEL_MEDICAL_ORDER)
  panelMedicalOrderAPI!: PanelAPI;

  @Inject(MedicineViewFragmentAliases.PANEL_LOADER)
  panelLoaderAPI!: PanelAPI;

  @Inject(MedicineViewFragmentAliases.PANEL_CARD)
  panelCardAPI!: PanelAPI;

  @Inject(MedicineViewFragmentAliases.FORM_MAIN_INFORMATIONS)
  formInformationsAPI!: FormAPI;

  @Inject(MedicineViewFragmentAliases.FORM_PERIOD_OF_VALIDITY)
  formPeriodValidityAPI!: FormAPI;

  @Inject(MedicineViewFragmentAliases.FORM_ADDICIONAL_INFORMATIONS)
  formAddicionalInformationsAPI!: FormAPI;

  @Inject(MedicineViewFragmentAliases.FORM_MEDICAL_ORDER)
  formMedicalOrderAPI!: FormAPI;

  @Inject(MedicineViewFragmentAliases.FIELD_END_DATE)
  fieldEndDateAPI!: FieldAPI;

  @Inject(MedicineViewFragmentAliases.FIELD_HOURS)
  fieldHoursAPI!: FieldAPI;

  @Inject(MedicineViewFragmentAliases.INPUT_START_DATE)
  inputStartDateAPI!: InputAPI;

  @Inject(MedicineViewFragmentAliases.INPUT_END_DATE)
  inputEndDateAPI!: InputAPI;

  @OnMount(MedicineViewFragmentAliases.PANEL_CARD)
  handleMountPanelCard(): void {
    InformationBarUtils.collapsedBar = true;
    const { medicineId, patientId  } = this.historyService.getCurrentRouteParams()
    this.fetchMedicineDetails(String(medicineId), String(patientId))
  }

  @OnMount(MedicineViewFragmentAliases.FORM_MAIN_INFORMATIONS)
  handleMountFormInformations(): void {
    const medicineView = this.context.get('medicineView') as MedicineViewResponseType
    if(medicineView) {
      const { applicationRoute, material, unitMeasurement } = medicineView
      const dose = String(medicineView.dose) || ''

      this.formInformationsAPI.setValue({
        applicationRoute,
        dose,
        material,
        unitMeasurement
      })
    }
  }

  @OnMount(MedicineViewFragmentAliases.FORM_PERIOD_OF_VALIDITY)
  handleMountFormPeriodOfValidity(): void {
    const medicineView = this.context.get('medicineView') as MedicineViewResponseType
    if(medicineView) {
      const endDate = moment(String(medicineView.endDate)).utc().format(ISO_DATE_INPUT)
      const startDate = moment(String(medicineView.startDate)).utc().format(ISO_DATE_INPUT)
      const {administration, duration, hours, period} = medicineView

      this.formPeriodValidityAPI.setValue({
        administration,
        duration,
        endDate,
        hours,
        period,
        startDate,
      })

      this.inputStartDateAPI.setValue(startDate)
      this.inputEndDateAPI.setValue(endDate)
      this.showFieldsByDurationType(medicineView.durationType);
      this.showFieldsByAdministrationType(medicineView.administrationType);
    }
  }

  @OnMount(MedicineViewFragmentAliases.FORM_ADDICIONAL_INFORMATIONS)
  handleMountFormAddicionalInformations(): void {
    const medicineView = this.context.get('medicineView') as MedicineViewResponseType
    if(medicineView) {
      const { prescriber, prescriberName } = medicineView
      this.formAddicionalInformationsAPI.setValue({
        prescriber,
        prescriberName
      })
    }
  }

  @OnMount(MedicineViewFragmentAliases.FORM_MEDICAL_ORDER)
  handleMountFormMedicalOrder(): void {
    const medicineView = this.context.get('medicineView') as MedicineViewResponseType
    if(medicineView) {
      const { medicalOrder } = medicineView;
      this.formMedicalOrderAPI.setValue({
        medicalOrder
      })
    }
  }

  @OnMount(MedicineViewFragmentAliases.CONTEXTUAL_BAR_MEDICINE_VIEW)
  handleMountContextualBar(): void {
    const medicineView = this.context.get('medicineView') as MedicineViewResponseType
    this.showFieldMedicalOrderIfNotEmpty(medicineView?.medicalOrder)
  }

  private showFieldsByDurationType(duration: string): void {
    this.fieldEndDateAPI.setVisible(duration === DurationType.PROGRAMMED)
  }

  private showFieldMedicalOrderIfNotEmpty(medicalOrder: string): void {
    this.panelMedicalOrderAPI.setVisible(!!medicalOrder)
  }

  private showFieldsByAdministrationType(administration: string): void {
    this.fieldHoursAPI.setVisible(administration === AdministrationType.AS_START_TIME)
  }

  private fetchMedicineDetails(medicineId: string, patientId: string): void {
    this.panelLoaderAPI.setVisible(true)
    this.panelCardAPI.setVisible(false)
    HttpService.get(Endpoint.GET_CPOE_MEDICINE_DETAILS, { medicineId, patientId })
      .then((response: HttpResponse) => {
        const medicineView = response.body as MedicineViewResponseType;
        this.context.set({ medicineView })
      }).catch((err) => {
        this.showToast(err?.body, ToastLevel.ERROR)
      }).finally(() => {
        this.panelLoaderAPI.setVisible(false)
        this.panelCardAPI.setVisible(true)
      })
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'medicine-view',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }
}

export default MedicineViewController;